// Email utilities for wrapping content in branded HTML email templates
// Now supports dynamic company config from database via CompanyConfig
// Falls back to hardcoded values for backward compatibility
//
// IMPORTANT: Company name must be "LifeTime Movers" (with capital T)
// CAN-SPAM requires full physical address including ZIP code

import type { CompanyConfig } from './companyConfig';

// Default config used when no CompanyConfig is passed (backward compatibility)
const DEFAULT_EMAIL_CONFIG = {
  companyName: 'LifeTime Movers',
  logoUrl: 'https://www.lifetimemovers.us/images/logo-email.jpg',
  address: '2021 Midwest Rd STE 200, Oak Brook, IL 60523',
  phone: '(630) 686-9987',
  adminEmail: 'info@lifetimemovers.us',
  replyTo: 'reservations@lifetimemovers.us',
  dotNumber: '3733668',
  mcNumber: '1318959',
  websiteUrl: 'https://www.lifetimemovers.us',
};

function resolveEmailConfig(companyConfig?: CompanyConfig) {
  if (!companyConfig) return DEFAULT_EMAIL_CONFIG;
  return {
    companyName: companyConfig.companyName,
    logoUrl: companyConfig.logoEmailUrl,
    address: companyConfig.companyAddress,
    phone: companyConfig.companyPhone,
    adminEmail: companyConfig.companyEmail,
    replyTo: companyConfig.defaultReplyTo,
    dotNumber: companyConfig.dotNumber,
    mcNumber: companyConfig.mcNumber,
    websiteUrl: companyConfig.websiteUrl,
  };
}

interface EmailWrapperStyles {
  headerBackground?: string;
  primaryColor?: string;
  secondaryColor?: string;
  buttonColor?: string;
  fontFamily?: string;
}

/**
 * Wraps email content in a complete HTML email structure with inline styles
 * This ensures consistent rendering across all email clients
 * 
 * @param content - HTML content to wrap
 * @param styles - Optional style overrides
 * @param templateName - Optional template name shown in header
 * @param companyConfig - Optional CompanyConfig from useCompanyConfig() for dynamic branding
 */
export function wrapEmailContent(
  content: string,
  styles: EmailWrapperStyles = {},
  templateName?: string,
  companyConfig?: CompanyConfig
): string {
  const emailConfig = resolveEmailConfig(companyConfig);
  const {
    headerBackground = '#1a1a1a',
    primaryColor = '#3b82f6',
    secondaryColor = '#6b7280',
    buttonColor = '#3b82f6',
    fontFamily = 'Arial, sans-serif'
  } = styles;

  // Add inline styles to all content elements
  let styledContent = content;
  
  // Convert empty paragraphs to spaced paragraphs for email client compatibility
  styledContent = styledContent.replace(/<p><br\s*\/?><\/p>/gi, '<p>&nbsp;</p>');
  styledContent = styledContent.replace(/<p><\/p>/g, '<p>&nbsp;</p>');
  styledContent = styledContent.replace(/<p>\s*<\/p>/g, '<p>&nbsp;</p>');
  
  // Add inline styles to heading elements
  styledContent = styledContent.replace(
    /<h1(\s[^>]*)?>/gi,
    `<h1$1 style="color: ${primaryColor}; font-size: 28px; font-weight: bold; margin: 0 0 16px 0; line-height: 1.3; font-family: ${fontFamily};">`
  );
  
  styledContent = styledContent.replace(
    /<h2(\s[^>]*)?>/gi,
    `<h2$1 style="color: ${primaryColor}; font-size: 24px; font-weight: bold; margin: 0 0 14px 0; line-height: 1.3; font-family: ${fontFamily};">`
  );
  
  styledContent = styledContent.replace(
    /<h3(\s[^>]*)?>/gi,
    `<h3$1 style="color: ${secondaryColor}; font-size: 20px; font-weight: 600; margin: 0 0 12px 0; line-height: 1.3; font-family: ${fontFamily};">`
  );
  
  // Add inline styles to paragraph elements
  styledContent = styledContent.replace(
    /<p(\s[^>]*)?>/gi,
    `<p$1 style="color: #333333; font-size: 16px; line-height: 1.6; margin: 0 0 12px 0; font-family: ${fontFamily};">`
  );
  
  // Add inline styles to lists
  styledContent = styledContent.replace(
    /<ul(\s[^>]*)?>/gi,
    `<ul$1 style="margin: 0 0 16px 0; padding-left: 24px; list-style-type: disc; font-family: ${fontFamily};">`
  );
  
  styledContent = styledContent.replace(
    /<ol(\s[^>]*)?>/gi,
    `<ol$1 style="margin: 0 0 16px 0; padding-left: 24px; list-style-type: decimal; font-family: ${fontFamily};">`
  );
  
  styledContent = styledContent.replace(
    /<li(\s[^>]*)?>/gi,
    `<li$1 style="margin-bottom: 8px; color: #333333; font-size: 16px; line-height: 1.6; font-family: ${fontFamily};">`
  );

  const websiteDomain = emailConfig.websiteUrl.replace('https://', '').replace('http://', '');

  // Build complete email HTML
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>${templateName || emailConfig.companyName}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #ffffff; font-family: ${fontFamily};">
  <table role="presentation" style="width: 100%; border-collapse: collapse; background-color: #ffffff;">
    <tr>
      <td style="padding: 20px 0;">
        <table role="presentation" style="width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-collapse: collapse;">
          <!-- Header -->
          <tr>
            <td style="background-color: #ffffff; padding: 20px 30px; border-bottom: 2px solid #e5e7eb;">
              <table role="presentation" style="width: 100%; border-collapse: collapse;">
                <tr>
                  <!-- Logo -->
                  <td style="width: 80px; vertical-align: middle;">
                    <img src="${emailConfig.logoUrl}" alt="${emailConfig.companyName}" style="width: 70px; height: auto; display: block;" />
                  </td>
                  <!-- Company Info -->
                  <td style="vertical-align: middle; padding-left: 15px;">
                    <div style="font-size: 18px; font-weight: bold; color: #1e3a5f; font-family: ${fontFamily};">${emailConfig.companyName}</div>
                    <div style="font-size: 11px; color: #6b7280; font-family: ${fontFamily}; margin-top: 2px;">${emailConfig.address}</div>
                    <div style="font-size: 11px; color: #6b7280; font-family: ${fontFamily};">${emailConfig.phone} | ${emailConfig.adminEmail}</div>
                  </td>
                  <!-- DOT/MC Numbers -->
                  <td style="text-align: right; vertical-align: middle; width: 100px;">
                    <div style="font-size: 11px; color: #6b7280; font-family: ${fontFamily};">DOT: ${emailConfig.dotNumber}</div>
                    <div style="font-size: 11px; color: #6b7280; font-family: ${fontFamily};">MC: ${emailConfig.mcNumber}</div>
                  </td>
                </tr>
              </table>
              ${templateName ? `<div style="text-align: center; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;"><span style="color: #3b82f6; font-size: 14px; font-weight: 600; font-family: ${fontFamily};">${templateName}</span></div>` : ''}
            </td>
          </tr>
          
          <!-- Content -->
          <tr>
            <td style="padding: 40px;">
              ${styledContent}
            </td>
          </tr>
          
          <!-- Footer with CAN-SPAM compliant physical address -->
          <tr>
            <td style="background-color: #f9fafb; padding: 30px 40px; text-align: center; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin: 0 0 8px 0; font-family: ${fontFamily};">
                <strong>${emailConfig.companyName}</strong><br>
                ${emailConfig.address}<br>
                Phone: ${emailConfig.phone}<br>
                Email: ${emailConfig.adminEmail}<br>
                <a href="${emailConfig.websiteUrl}" style="color: ${primaryColor}; text-decoration: none;">${websiteDomain}</a>
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 12px 0 0 0; font-family: ${fontFamily};">
                DOT: ${emailConfig.dotNumber} | MC: ${emailConfig.mcNumber}
              </p>
              <p style="color: #9ca3af; font-size: 12px; margin: 8px 0 0 0; font-family: ${fontFamily};">
                © ${new Date().getFullYear()} ${emailConfig.companyName}. All rights reserved.
              </p>
              <p style="color: #9ca3af; font-size: 11px; margin: 16px 0 0 0; font-family: ${fontFamily};">
                <a href="{{unsubscribe_link}}" style="color: #9ca3af; text-decoration: underline;">Unsubscribe from these emails</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Legacy function - kept for backward compatibility
 * Use wrapEmailContent() for new implementations
 */
export function addInlineStylesToLists(html: string): string {
  let styledHtml = html;
  
  styledHtml = styledHtml.replace(
    /<ul(\s[^>]*)?>/gi,
    '<ul$1 style="margin: 0 0 16px 0; padding-left: 24px; list-style-type: disc;">'
  );
  
  styledHtml = styledHtml.replace(
    /<ol(\s[^>]*)?>/gi,
    '<ol$1 style="margin: 0 0 16px 0; padding-left: 24px; list-style-type: decimal;">'
  );
  
  styledHtml = styledHtml.replace(
    /<li(\s[^>]*)?>/gi,
    '<li$1 style="margin-bottom: 8px;">'
  );
  
  return styledHtml;
}
